import { createSignal, onMount, createEffect, Show } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";

export default function DnsSettings(props: {
    onBack?: () => void;
    onSave?: (settings: any) => void;
    setSystemDnsRef?: (ref: any) => void;
}) {
    // DNS option refs
    let systemDnsRef;
    let googleDnsRef;
    let cloudflareDnsRef;
    let quad9DnsRef;
    let customDnsRef;
    // DNS input field refs (8 total)
    let dnsInput1Ref, dnsInput2Ref, dnsInput3Ref, dnsInput4Ref;
    let dnsInput5Ref, dnsInput6Ref, dnsInput7Ref, dnsInput8Ref;

    // Keypad refs
    let keypad1Ref, keypad2Ref, keypad3Ref, keypad4Ref, keypad5Ref;
    let keypad6Ref, keypad7Ref, keypad8Ref, keypad9Ref, keypad0Ref;
    let keypadDeleteRef, keypadSaveRef;

    // State management
    const [dnsType, setDnsType] = createSignal("system"); // "system" or "custom"
    const [customDns, setCustomDns] = createSignal({
        primary1: "8", primary2: "8", primary3: "8", primary4: "8",
        secondary1: "8", secondary2: "8", secondary3: "4", secondary4: "4"
    });

    // Current field tracking for auto-advance (visual only, focus stays on keyboard)
    const [currentFieldIndex, setCurrentFieldIndex] = createSignal(0); // 0-7
    const [keyboardHasFocus, setKeyboardHasFocus] = createSignal(false); // Track if keyboard should be focused
    const [manuallySelectedField, setManuallySelectedField] = createSignal(-1); // -1 means no manual selection



    const loadSelectedDnsId = () => {
        return localStorage.getItem("selectedDnsId") || "system"; // Default to "system"
    };

    // Set up the signal
    const [selectedDnsId, setSelectedDnsId] = createSignal(loadSelectedDnsId());

    // Save to local storage whenever selectedDnsId changes
    const saveSelectedDnsId = (id) => {
        localStorage.setItem("selectedDnsId", id);
    };

    // Watch for changes to the selectedDnsId signal
    createEffect(() => {
        saveSelectedDnsId(selectedDnsId());
    });

    onMount(() => {
        props.setSystemDnsRef?.(systemDnsRef);
    });


    // Create an effect to log changes for debugging
    createEffect(() => {
        console.log('Field index changed to:', currentFieldIndex());
        console.log('Manually selected field:', manuallySelectedField());
    });
    const fieldNames = ['primary1', 'primary2', 'primary3', 'primary4', 'secondary1', 'secondary2', 'secondary3', 'secondary4'];
    const fieldRefs = () => [dnsInput1Ref, dnsInput2Ref, dnsInput3Ref, dnsInput4Ref, dnsInput5Ref, dnsInput6Ref, dnsInput7Ref, dnsInput8Ref];

    // Helper function to get DNS option button style with forced reactivity
    const getDnsOptionStyle = (optionType: string) => {
        // Force reactivity by accessing all relevant signals
        const currentDnsType = dnsType();
        const currentCustomDns = customDns();

        let isSelected = false;

        if (optionType === "system") {
            isSelected = currentDnsType === "system";
        } else if (optionType === "google") {
            isSelected = currentDnsType === "custom" &&
                currentCustomDns.primary1 === "8" && currentCustomDns.primary2 === "8" &&
                currentCustomDns.primary3 === "8" && currentCustomDns.primary4 === "8" &&
                currentCustomDns.secondary1 === "8" && currentCustomDns.secondary2 === "8" &&
                currentCustomDns.secondary3 === "4" && currentCustomDns.secondary4 === "4";
        } else if (optionType === "cloudflare") {
            isSelected = currentDnsType === "custom" &&
                currentCustomDns.primary1 === "1" && currentCustomDns.primary2 === "1" &&
                currentCustomDns.primary3 === "1" && currentCustomDns.primary4 === "1" &&
                currentCustomDns.secondary1 === "1" && currentCustomDns.secondary2 === "0" &&
                currentCustomDns.secondary3 === "0" && currentCustomDns.secondary4 === "1";
        } else if (optionType === "quad9") {
            isSelected = currentDnsType === "custom" &&
                currentCustomDns.primary1 === "9" && currentCustomDns.primary2 === "9" &&
                currentCustomDns.primary3 === "9" && currentCustomDns.primary4 === "9" &&
                currentCustomDns.secondary1 === "149" && currentCustomDns.secondary2 === "112" &&
                currentCustomDns.secondary3 === "112" && currentCustomDns.secondary4 === "112";
        } else if (optionType === "custom") {
            isSelected = currentDnsType === "custom" &&
                !(currentCustomDns.primary1 === "8" && currentCustomDns.primary2 === "8" &&
                    currentCustomDns.primary3 === "8" && currentCustomDns.primary4 === "8" &&
                    currentCustomDns.secondary1 === "8" && currentCustomDns.secondary2 === "8" &&
                    currentCustomDns.secondary3 === "4" && currentCustomDns.secondary4 === "4") &&
                !(currentCustomDns.primary1 === "1" && currentCustomDns.primary2 === "1" &&
                    currentCustomDns.primary3 === "1" && currentCustomDns.primary4 === "1" &&
                    currentCustomDns.secondary1 === "1" && currentCustomDns.secondary2 === "0" &&
                    currentCustomDns.secondary3 === "0" && currentCustomDns.secondary4 === "1") &&
                !(currentCustomDns.primary1 === "9" && currentCustomDns.primary2 === "9" &&
                    currentCustomDns.primary3 === "9" && currentCustomDns.primary4 === "9" &&
                    currentCustomDns.secondary1 === "149" && currentCustomDns.secondary2 === "112" &&
                    currentCustomDns.secondary3 === "112" && currentCustomDns.secondary4 === "112");
        }

        console.log(`DNS Option ${optionType}: isSelected = ${isSelected}, dnsType = ${currentDnsType}`);

        console.log('isSelected ', isSelected);


        return {
            borderRadius: 8,
            width: 480,
            height: 50,
            padding: 10,
            focus: { color: hexColor("#128DD3") },
            // color: isSelected ? hexColor("#2a4a2a") : hexColor("#484e55"),
            color: hexColor("#484e55"),
        };
    };

    // Helper function to get field style - only highlight manually selected fields
    const getFieldStyle = (fieldIndex: number) => {
        const isManuallySelected = manuallySelectedField() === fieldIndex;

        return {
            borderRadius: 6,
            width: 80,
            height: 45,
            margin: 5,
            padding: 8,
            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
            color: isManuallySelected ? hexColor("#ff6600") : hexColor("#484e55"),
            border: {
                width: isManuallySelected ? 4 : 1,
                color: isManuallySelected ? hexColor("#ff9500") : hexColor("#666666")
            }
        };
    };

    // Helper function to get bullet point for DNS options
    const getDnsBullet = (optionType: string) => {
        let isSelected = false;

        if (optionType === "system") {
            isSelected = dnsType() === "system";
        } else if (optionType === "google") {
            isSelected = isGoogleDns();
        } else if (optionType === "cloudflare") {
            isSelected = isCloudflareDns();
        } else if (optionType === "quad9") {
            isSelected = isQuad9Dns();
        } else if (optionType === "custom") {
            isSelected = isCustomDnsSelected();
        }

        return isSelected ? "● " : "○ ";
    };

    // Helper functions for preset DNS detection
    const isGoogleDns = () => {
        const dns = customDns();
        return dnsType() === "custom" &&
            dns.primary1 === "8" && dns.primary2 === "8" &&
            dns.primary3 === "8" && dns.primary4 === "8" &&
            dns.secondary1 === "8" && dns.secondary2 === "8" &&
            dns.secondary3 === "4" && dns.secondary4 === "4";
    };

    const isCloudflareDns = () => {
        const dns = customDns();
        return dnsType() === "custom" &&
            dns.primary1 === "1" && dns.primary2 === "1" &&
            dns.primary3 === "1" && dns.primary4 === "1" &&
            dns.secondary1 === "1" && dns.secondary2 === "0" &&
            dns.secondary3 === "0" && dns.secondary4 === "1";
    };

    const isQuad9Dns = () => {
        const dns = customDns();
        return dnsType() === "custom" &&
            dns.primary1 === "9" && dns.primary2 === "9" &&
            dns.primary3 === "9" && dns.primary4 === "9" &&
            dns.secondary1 === "149" && dns.secondary2 === "112" &&
            dns.secondary3 === "112" && dns.secondary4 === "112";
    };

    const isCustomDnsSelected = () => {
        return dnsType() === "custom" && !isGoogleDns() && !isCloudflareDns() && !isQuad9Dns();
    };

    // Auto-advance keypad functionality - NO COLOR HIGHLIGHTING
    const handleKeypadDigit = (digit: string) => {
        const currentFieldIndex_val = currentFieldIndex();
        const currentField = fieldNames[currentFieldIndex_val];
        const newValue = digit;

        console.log(`Typing digit: ${digit}, Current field: ${currentField} (index ${currentFieldIndex_val})`);

        // Clear any manual selection when typing
        setManuallySelectedField(-1);

        // Validate range (0-255 for single digits, we'll handle multi-digit later)
        if (parseInt(newValue) >= 0 && parseInt(newValue) <= 9) {
            // Update the DNS value
            setCustomDns(prev => {
                const updated = {
                    ...prev,
                    [currentField]: newValue
                };
                console.log('Updated DNS:', updated);
                return updated;
            });

            // Auto-advance to next field (no visual highlighting)
            const nextIndex = currentFieldIndex_val + 1;
            if (nextIndex < fieldNames.length) {
                console.log(`Moving to next field: ${fieldNames[nextIndex]} (index ${nextIndex})`);
                setCurrentFieldIndex(nextIndex);
            } else {
                // Reset to first field if we've reached the end
                console.log('Reached end, resetting to first field');
                setCurrentFieldIndex(0);
            }

            // Save settings
            setTimeout(() => {
                saveDnsSettings();
            }, 100);
        }
    };

    // Handle backspace/delete
    const handleKeypadDelete = () => {
        const currentField = fieldNames[currentFieldIndex()];

        console.log(`Deleting from field: ${currentField} (index ${currentFieldIndex()})`);

        // Clear current field
        setCustomDns(prev => {
            const updated = {
                ...prev,
                [currentField]: '0'
            };
            console.log('Updated DNS after delete:', updated);
            return updated;
        });

        // Go back to previous field (visual indicator only)
        const prevIndex = currentFieldIndex() - 1;
        if (prevIndex >= 0) {
            console.log(`Moving to previous field: ${fieldNames[prevIndex]} (index ${prevIndex})`);
            setCurrentFieldIndex(prevIndex);
        } else {
            // Go to last field if we're at the beginning
            console.log('At beginning, moving to last field');
            setCurrentFieldIndex(fieldNames.length - 1);
        }

        // Save settings
        setTimeout(() => {
            saveDnsSettings();
        }, 100);
    };

    // Handle manual field selection - SHOW ORANGE HIGHLIGHTING
    const selectField = (fieldIndex: number) => {
        console.log(`Manually selecting field: ${fieldNames[fieldIndex]} (index ${fieldIndex})`);
        setCurrentFieldIndex(fieldIndex);
        setManuallySelectedField(fieldIndex); // Show orange highlight
        setKeyboardHasFocus(true);
        // Return focus to the center of keypad after a short delay
        setTimeout(() => {
            keypad5Ref?.setFocus?.();
        }, 100);
    };

    // Focus keyboard when custom DNS is selected
    const focusKeyboard = () => {
        setKeyboardHasFocus(true);
        // Reset to first field when focusing keyboard and clear manual selection
        setCurrentFieldIndex(0);
        setManuallySelectedField(-1);
        setTimeout(() => {
            keypad5Ref?.setFocus?.();
        }, 100);
    };

    // Save DNS settings
    const saveDnsSettings = () => {
        const dnsSettings = {
            type: dnsType(),
            customDns: customDns()
        };
        localStorage.setItem("dnsSettings", JSON.stringify(dnsSettings));
        console.log("DNS settings saved:", dnsSettings);
        props.onSave?.(dnsSettings);
    };

    // Load DNS settings
    const loadDnsSettings = () => {
        const savedSettings = localStorage.getItem("dnsSettings");
        if (savedSettings) {
            const parsed = JSON.parse(savedSettings);
            setDnsType(parsed.type || "system");
            if (parsed.customDns) {
                setCustomDns(parsed.customDns);
            }
            console.log("DNS settings loaded:", parsed);
        }
    };

    onMount(() => {
        loadDnsSettings();
        // systemDnsRef?.setFocus?.();
        // Initialize field index to 0 when component mounts
        setCurrentFieldIndex(0);
    });

    return (
        <View width={1200} height={700}>
            <Text x={0} y={0} style={{ fontSize: 35, color: hexColor("#ffffff") }}>
                DNS Settings
            </Text>

            <Text x={0} y={50} style={{ fontSize: 16, color: hexColor("#a0a1a3") }}>
                Choose how your device resolves website addresses. Most users should use System DNS or a preset.
            </Text>

            {/* Main Layout: Left side DNS options, Right side input/keypad */}
            <View y={100} style={{ display: "flex", flexDirection: "row" }}>

                {/* LEFT SIDE - DNS Options (500px width) */}
                <View width={500}>
                    {/* System DNS */}
                    <View
                        ref={systemDnsRef}
                        x={0}
                        y={0}
                        onDown={() => googleDnsRef?.setFocus?.()}
                        onRight={() => isCustomDnsSelected() && focusKeyboard()}
                        onLeft={() => props.onBack?.()}
                        onEnter={() => {
                            setDnsType("system");
                            saveDnsSettings();
                            setSelectedDnsId("system");
                        }}
                        style={getDnsOptionStyle("system")}
                    >
                        <Text x={10} y={15} style={{ fontSize: 20, fontWeight: "bold" }}>
                            {getDnsBullet("system")}System DNS (Default)
                        </Text>

                        {selectedDnsId() === "system" && (
                            <View x={380} y={0}>
                                <View
                                    src="https://i.ibb.co/FbFMtxMw/check.png"
                                    style={{ width: 25, height: 25, x: 45, y: 12 }}
                                />
                            </View>
                        )}
                    </View>

                    {/* Google DNS */}
                    <View
                        ref={googleDnsRef}
                        x={0}
                        y={70}
                        onUp={() => systemDnsRef?.setFocus?.()}
                        onDown={() => cloudflareDnsRef?.setFocus?.()}
                        onRight={() => isCustomDnsSelected() && focusKeyboard()}
                        onLeft={() => props.onBack?.()}
                        onEnter={() => {
                            setDnsType("custom");
                            setSelectedDnsId("google");
                            setCustomDns({
                                primary1: "8", primary2: "8", primary3: "8", primary4: "8",
                                secondary1: "8", secondary2: "8", secondary3: "4", secondary4: "4"
                            });
                            setTimeout(() => saveDnsSettings(), 50);
                        }}
                        style={getDnsOptionStyle("google")}
                    >
                        <Text x={10} y={15} style={{ fontSize: 18, fontWeight: "bold" }}>
                            {getDnsBullet("google")}Google DNS - 8.8.8.8
                        </Text>
                        {selectedDnsId() === "google" && (
                            <View x={380} y={0}>
                                <View
                                    src="https://i.ibb.co/FbFMtxMw/check.png"
                                    style={{ width: 25, height: 25, x: 45, y: 12 }}
                                />
                            </View>
                        )}


                    </View>

                    {/* Cloudflare DNS */}
                    <View
                        ref={cloudflareDnsRef}
                        x={0}
                        y={140}
                        onUp={() => googleDnsRef?.setFocus?.()}
                        onDown={() => quad9DnsRef?.setFocus?.()}
                        onRight={() => isCustomDnsSelected() && focusKeyboard()}
                        onLeft={() => props.onBack?.()}
                        onEnter={() => {
                            setDnsType("custom");
                            setSelectedDnsId("cloudflare");

                            setCustomDns({
                                primary1: "1", primary2: "1", primary3: "1", primary4: "1",
                                secondary1: "1", secondary2: "0", secondary3: "0", secondary4: "1"
                            });
                            setTimeout(() => saveDnsSettings(), 50);
                        }}
                        style={getDnsOptionStyle("cloudflare")}
                    >
                        <Text x={10} y={15} style={{ fontSize: 18, fontWeight: "bold" }}>
                            {getDnsBullet("cloudflare")}Cloudflare DNS - 1.1.1.1
                        </Text>
                        {selectedDnsId() === "cloudflare" && (
                            <View x={380} y={0}>
                                <View
                                    src="https://i.ibb.co/FbFMtxMw/check.png"
                                    style={{ width: 25, height: 25, x: 45, y: 12 }}
                                />
                            </View>
                        )}
                    </View>

                    {/* Quad9 DNS */}
                    <View
                        ref={quad9DnsRef}
                        x={0}
                        y={210}
                        onUp={() => cloudflareDnsRef?.setFocus?.()}
                        onDown={() => customDnsRef?.setFocus?.()}
                        onRight={() => isCustomDnsSelected() && focusKeyboard()}
                        onLeft={() => props.onBack?.()}
                        onEnter={() => {
                            setSelectedDnsId("quad9");
                            setDnsType("custom");
                            setCustomDns({
                                primary1: "9", primary2: "9", primary3: "9", primary4: "9",
                                secondary1: "149", secondary2: "112", secondary3: "112", secondary4: "112"
                            });
                            setTimeout(() => saveDnsSettings(), 50);
                        }}
                        style={getDnsOptionStyle("quad9")}
                    >
                        <Text x={10} y={15} style={{ fontSize: 18, fontWeight: "bold" }}>
                            {getDnsBullet("quad9")}Quad9 DNS - 9.9.9.9
                        </Text>
                        {selectedDnsId() === "quad9" && (
                            <View x={380} y={0}>
                                <View
                                    src="https://i.ibb.co/FbFMtxMw/check.png"
                                    style={{ width: 25, height: 25, x: 45, y: 12 }}
                                />
                            </View>
                        )}
                    </View>

                    {/* Custom DNS */}
                    <View
                        ref={customDnsRef}
                        x={0}
                        y={280}
                        onUp={() => quad9DnsRef?.setFocus?.()}
                        onRight={() => {
                            setDnsType("custom");
                            if (!isCustomDnsSelected()) {
                                setCustomDns({
                                    primary1: "0", primary2: "0", primary3: "0", primary4: "0",
                                    secondary1: "0", secondary2: "0", secondary3: "0", secondary4: "0"
                                });
                            }
                            // Always reset to first field when entering custom DNS
                            setCurrentFieldIndex(0);
                            setTimeout(() => {
                                saveDnsSettings();
                                focusKeyboard();
                            }, 100);
                        }}
                        onLeft={() => props.onBack?.()}
                        onEnter={() => {
                            setDnsType("custom");
                            setSelectedDnsId("custom");
                            if (!isCustomDnsSelected()) {
                                setCustomDns({
                                    primary1: "0", primary2: "0", primary3: "0", primary4: "0",
                                    secondary1: "0", secondary2: "0", secondary3: "0", secondary4: "0"
                                });
                            }
                            // Always reset to first field when entering custom DNS
                            setCurrentFieldIndex(0);
                            setTimeout(() => {
                                saveDnsSettings();
                                focusKeyboard();
                            }, 100);
                        }}
                        style={getDnsOptionStyle("custom")}
                    >
                        <Text x={10} y={15} style={{ fontSize: 18, fontWeight: "bold" }}>
                            {getDnsBullet("custom")}Custom DNS (Advanced)
                        </Text>
                        {selectedDnsId() === "custom" && (
                            <View x={380} y={0}>
                                <View
                                    src="https://i.ibb.co/FbFMtxMw/check.png"
                                    style={{ width: 25, height: 25, x: 45, y: 12 }}
                                />
                            </View>
                        )}
                    </View>
                </View>

                {/* RIGHT SIDE - DNS Input Fields + Keypad (650px width - made bigger) */}
                <Show when={isCustomDnsSelected()}>
                    <View x={520} width={650}>
                        {/* Primary DNS Input */}
                        <Text y={0} style={{ fontSize: 20, color: hexColor("#ffffff"), fontWeight: "bold" }}>Primary DNS:</Text>
                        <View y={35} style={{ display: "flex", flexDirection: "row" }}>
                            {/* Primary DNS Field 1 */}
                            <View
                                ref={dnsInput1Ref}
                                x={0}
                                onLeft={() => customDnsRef?.setFocus?.()}
                                onRight={() => dnsInput2Ref?.setFocus?.()}
                                onDown={() => dnsInput5Ref?.setFocus?.()}
                                onEnter={() => selectField(0)}
                                style={getFieldStyle(0)}
                            >
                                <Text x={25} y={12} style={{
                                    fontSize: 18,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: hexColor("#ffffff")
                                }}>
                                    {customDns().primary1}
                                </Text>
                            </View>
                            <Text x={95} y={15} style={{ fontSize: 20, color: hexColor("#ffffff") }}>.</Text>

                            {/* Primary DNS Field 2 */}
                            <View
                                ref={dnsInput2Ref}
                                x={115}
                                onLeft={() => dnsInput1Ref?.setFocus?.()}
                                onRight={() => dnsInput3Ref?.setFocus?.()}
                                onDown={() => dnsInput6Ref?.setFocus?.()}
                                onEnter={() => selectField(1)}
                                style={getFieldStyle(1)}
                            >
                                <Text x={25} y={12} style={{
                                    fontSize: 18,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: hexColor("#ffffff")
                                }}>
                                    {customDns().primary2}
                                </Text>
                            </View>
                            <Text x={210} y={15} style={{ fontSize: 20, color: hexColor("#ffffff") }}>.</Text>

                            {/* Primary DNS Field 3 */}
                            <View
                                ref={dnsInput3Ref}
                                x={230}
                                onLeft={() => dnsInput2Ref?.setFocus?.()}
                                onRight={() => dnsInput4Ref?.setFocus?.()}
                                onDown={() => dnsInput7Ref?.setFocus?.()}
                                onEnter={() => selectField(2)}
                                style={getFieldStyle(2)}
                            >
                                <Text x={25} y={12} style={{
                                    fontSize: 18,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: hexColor("#ffffff")
                                }}>
                                    {customDns().primary3}
                                </Text>
                            </View>
                            <Text x={325} y={15} style={{ fontSize: 20, color: hexColor("#ffffff") }}>.</Text>

                            {/* Primary DNS Field 4 */}
                            <View
                                ref={dnsInput4Ref}
                                x={345}
                                onLeft={() => dnsInput3Ref?.setFocus?.()}
                                onDown={() => dnsInput8Ref?.setFocus?.()}
                                onEnter={() => selectField(3)}
                                style={getFieldStyle(3)}
                            >
                                <Text x={25} y={12} style={{
                                    fontSize: 18,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: hexColor("#ffffff")
                                }}>
                                    {customDns().primary4}
                                </Text>
                            </View>
                        </View>

                        {/* Secondary DNS Input */}
                        <Text y={110} style={{ fontSize: 20, color: hexColor("#ffffff"), fontWeight: "bold" }}>Secondary DNS:</Text>
                        <View y={145} style={{ display: "flex", flexDirection: "row" }}>
                            {/* Secondary DNS Fields */}
                            <View
                                ref={dnsInput5Ref}
                                x={0}
                                onLeft={() => customDnsRef?.setFocus?.()}
                                onRight={() => dnsInput6Ref?.setFocus?.()}
                                onUp={() => dnsInput1Ref?.setFocus?.()}
                                onDown={() => keypad1Ref?.setFocus?.()}
                                onEnter={() => selectField(4)}
                                style={getFieldStyle(4)}
                            >
                                <Text x={25} y={12} style={{
                                    fontSize: 18,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: hexColor("#ffffff")
                                }}>
                                    {customDns().secondary1}
                                </Text>
                            </View>
                            <Text x={95} y={15} style={{ fontSize: 20, color: hexColor("#ffffff") }}>.</Text>

                            <View
                                ref={dnsInput6Ref}
                                x={115}
                                onLeft={() => dnsInput5Ref?.setFocus?.()}
                                onRight={() => dnsInput7Ref?.setFocus?.()}
                                onUp={() => dnsInput2Ref?.setFocus?.()}
                                onDown={() => keypad2Ref?.setFocus?.()}
                                onEnter={() => selectField(5)}
                                style={getFieldStyle(5)}
                            >
                                <Text x={25} y={12} style={{
                                    fontSize: 18,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: hexColor("#ffffff")
                                }}>
                                    {customDns().secondary2}
                                </Text>
                            </View>
                            <Text x={210} y={15} style={{ fontSize: 20, color: hexColor("#ffffff") }}>.</Text>

                            <View
                                ref={dnsInput7Ref}
                                x={230}
                                onLeft={() => dnsInput6Ref?.setFocus?.()}
                                onRight={() => dnsInput8Ref?.setFocus?.()}
                                onUp={() => dnsInput3Ref?.setFocus?.()}
                                onDown={() => keypad3Ref?.setFocus?.()}
                                onEnter={() => selectField(6)}
                                style={getFieldStyle(6)}
                            >
                                <Text x={25} y={12} style={{
                                    fontSize: 18,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: hexColor("#ffffff")
                                }}>
                                    {customDns().secondary3}
                                </Text>
                            </View>
                            <Text x={325} y={15} style={{ fontSize: 20, color: hexColor("#ffffff") }}>.</Text>

                            <View
                                ref={dnsInput8Ref}
                                x={345}
                                onLeft={() => dnsInput7Ref?.setFocus?.()}
                                onUp={() => dnsInput4Ref?.setFocus?.()}
                                onDown={() => keypad5Ref?.setFocus?.()}
                                onEnter={() => selectField(7)}
                                style={getFieldStyle(7)}
                            >
                                <Text x={25} y={12} style={{
                                    fontSize: 18,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: hexColor("#ffffff")
                                }}>
                                    {customDns().secondary4}
                                </Text>
                            </View>
                        </View>

                        {/* Number Keypad - Made bigger and better positioned */}
                        <View y={220}>
                            <Text style={{ fontSize: 16, color: hexColor("#ffffff"), fontWeight: "bold" }}>
                                Number Keypad: (Type directly or use arrow keys)
                            </Text>

                            {/* Active Field Indicator */}
                            <View y={25}>
                                <Text style={{ fontSize: 14, color: hexColor("#ff9500"), fontWeight: "bold" }}>
                                    → Currently editing: {fieldNames[currentFieldIndex()]} (field {currentFieldIndex() + 1}/8)
                                </Text>
                            </View>

                            {/* Keypad Grid - Made wider and less high with rectangular buttons */}
                            <View y={55}>
                                {/* Row 1: 1, 2, 3 */}
                                <View gap={8} style={{ display: "flex", flexDirection: "row" }}>
                                    <View
                                        ref={keypad1Ref}
                                        // x={0}
                                        onRight={() => keypad2Ref?.setFocus?.()}
                                        onDown={() => keypad4Ref?.setFocus?.()}
                                        onUp={() => dnsInput5Ref?.setFocus?.()}
                                        onLeft={() => customDnsRef?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('1')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>1</Text>
                                    </View>

                                    <View
                                        ref={keypad2Ref}
                                        x={136}
                                        onLeft={() => keypad1Ref?.setFocus?.()}
                                        onRight={() => keypad3Ref?.setFocus?.()}
                                        onDown={() => keypad5Ref?.setFocus?.()}
                                        onUp={() => dnsInput6Ref?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('2')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>2</Text>
                                    </View>

                                    <View
                                        ref={keypad3Ref}
                                        x={272}
                                        onLeft={() => keypad2Ref?.setFocus?.()}
                                        onDown={() => keypad6Ref?.setFocus?.()}
                                        onUp={() => dnsInput7Ref?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('3')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>3</Text>
                                    </View>
                                </View>

                                {/* Row 2: 4, 5, 6 */}
                                <View gap={8} y={69} style={{ display: "flex", flexDirection: "row" }}>
                                    <View
                                        ref={keypad4Ref}
                                        x={0}
                                        onRight={() => keypad5Ref?.setFocus?.()}
                                        onUp={() => keypad1Ref?.setFocus?.()}
                                        onDown={() => keypad7Ref?.setFocus?.()}
                                        onLeft={() => customDnsRef?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('4')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>4</Text>
                                    </View>

                                    <View
                                        ref={keypad5Ref}
                                        x={136}
                                        onLeft={() => keypad4Ref?.setFocus?.()}
                                        onRight={() => keypad6Ref?.setFocus?.()}
                                        onUp={() => keypad2Ref?.setFocus?.()}
                                        onDown={() => keypad8Ref?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('5')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>5</Text>
                                    </View>

                                    <View
                                        ref={keypad6Ref}
                                        x={272}
                                        onLeft={() => keypad5Ref?.setFocus?.()}
                                        onUp={() => keypad3Ref?.setFocus?.()}
                                        onDown={() => keypad9Ref?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('6')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>6</Text>
                                    </View>
                                </View>

                                {/* Row 3: 7, 8, 9 */}
                                <View gap={8} y={139} style={{ display: "flex", flexDirection: "row" }}>
                                    <View
                                        ref={keypad7Ref}
                                        x={0}
                                        onRight={() => keypad8Ref?.setFocus?.()}
                                        onUp={() => keypad4Ref?.setFocus?.()}
                                        onDown={() => keypadDeleteRef?.setFocus?.()}
                                        onLeft={() => customDnsRef?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('7')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>7</Text>
                                    </View>

                                    <View
                                        ref={keypad8Ref}
                                        x={136}
                                        onLeft={() => keypad7Ref?.setFocus?.()}
                                        onRight={() => keypad9Ref?.setFocus?.()}
                                        onUp={() => keypad5Ref?.setFocus?.()}
                                        onDown={() => keypad0Ref?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('8')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>8</Text>
                                    </View>

                                    <View
                                        ref={keypad9Ref}
                                        x={272}
                                        onLeft={() => keypad8Ref?.setFocus?.()}
                                        onUp={() => keypad6Ref?.setFocus?.()}
                                        onDown={() => keypadSaveRef?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('9')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>9</Text>
                                    </View>
                                </View>

                                {/* Row 4: Delete, 0, Save */}
                                <View gap={8} y={209} style={{ display: "flex", flexDirection: "row" }}>
                                    <View
                                        ref={keypadDeleteRef}
                                        x={0}
                                        onRight={() => keypad0Ref?.setFocus?.()}
                                        onUp={() => keypad7Ref?.setFocus?.()}
                                        onLeft={() => customDnsRef?.setFocus?.()}
                                        onEnter={() => handleKeypadDelete()}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#ff6b6b"), border: { width: 2, color: hexColor("#ff6b6b") } },
                                            color: hexColor("#553333"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <View
                                            src="https://i.ibb.co/wh6n5N3X/backspace.png"
                                            style={{ width: 30, height: 30, x: 35, y: 15 }}
                                        />
                                    </View>

                                    <View
                                        ref={keypad0Ref}
                                        x={136}
                                        onLeft={() => keypadDeleteRef?.setFocus?.()}
                                        onRight={() => keypadSaveRef?.setFocus?.()}
                                        onUp={() => keypad8Ref?.setFocus?.()}
                                        onEnter={() => handleKeypadDigit('0')}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#128DD3"), border: { width: 2, color: hexColor("#128DD3") } },
                                            color: hexColor("#484e55"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <Text x={42} y={20} style={{ fontSize: 20, fontWeight: "bold", textAlign: "center" }}>0</Text>
                                    </View>

                                    <View
                                        ref={keypadSaveRef}
                                        x={272}
                                        onLeft={() => keypad0Ref?.setFocus?.()}
                                        onUp={() => keypad9Ref?.setFocus?.()}
                                        onEnter={() => {
                                            saveDnsSettings();
                                            // Focus back to DNS options
                                            setTimeout(() => {
                                                customDnsRef?.setFocus?.();
                                            }, 100);
                                        }}
                                        style={{
                                            borderRadius: 4,
                                            width: 100,
                                            height: 60,
                                            margin: 8,
                                            padding: 12,
                                            focus: { color: hexColor("#66cc66"), border: { width: 2, color: hexColor("#66cc66") } },
                                            color: hexColor("#335533"),
                                            border: { width: 1, color: hexColor("#666666") }
                                        }}
                                    >
                                        <View
                                            src="https://i.ibb.co/FbFMtxMw/check.png"
                                            style={{ width: 30, height: 30, x: 35, y: 15 }}
                                        />
                                    </View>
                                </View>
                            </View>
                        </View>


                    </View>
                </Show>
            </View>

            {/* Current Status Display - Removed */}
        </View>
    );
}